import React from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import { ArrowLeft, CheckCircle, Briefcase, TrendingUp, AlertTriangle, ShieldCheck, Settings, DollarSign, Zap } from 'lucide-react';

// Enhanced Story Structure
interface KeyIntervention {
  title: string;
  description: string;
  icon?: React.ElementType;
}

interface ResultHighlight {
  metric: string;
  label: string;
  icon?: React.ElementType;
}

interface StoryContent {
  slug: string;
  metaTitle: string;
  metaDescription: string;
  heroImageUrl: string;
  pageTitle: string;
  executiveSummary: string;
  clientInfo?: {
    name: string;
    industry: string;
    challengeSummary: string;
  };
  challengeSection: {
    title: string;
    content: string; // Markdown supported
  };
  solutionSection: {
    title: string;
    intro: string; // Markdown supported
    keyInterventions: KeyIntervention[];
  };
  resultsSection: {
    title: string;
    summary: string; // Markdown supported
    highlights: ResultHighlight[];
  };
}

const stories: StoryContent[] = [
  {
    slug: "cost-reduction",
    metaTitle: "Case Study: AWS Cost Optimization - Kult.io",
    metaDescription: "Review how Kult.io provided a solution for a global application developer to reduce AWS costs by 60% through infrastructure optimization and improved monitoring.",
    heroImageUrl: "https://picsum.photos/seed/aws-cost-reduction-kultio/1200/400",
    pageTitle: "AWS Cost Reduction: A 60% Optimization for a Global Developer",
    executiveSummary: "A global application developer experienced significant AWS costs from their active development process, impacting business profitability. Kult.io provided a solution involving a multi-faceted optimization of their AWS infrastructure. This project resulted in a 60% reduction in operational costs and established a system for ongoing fiscal management in the cloud.",
    clientInfo: {
      name: "Global Application Developer",
      industry: "Software Development & Cloud Services",
      challengeSummary: "High AWS operational costs from extensive, multi-environment development work."
    },
    challengeSection: {
      title: "The Challenge: Managing Cloud Expenditures",
      content: "Our client, a global developer of applications, utilized multiple AWS environments for their development, staging, and production work. This active process, essential for their business, led to complex and substantial AWS service costs. These expenditures began to affect profits, a significant issue for the application's financial health. The company asked Kult.io to provide a solution: **reduce cloud service costs substantially, without affecting development time or system performance.**"
    },
    solutionSection: {
      title: "Our Solution: Infrastructure Optimization & Process Improvement",
      intro: "Our team conducted a detailed analysis of the client's AWS infrastructure. We identified key areas for optimization, focusing on structural improvements, service utilization, and financial management processes:",
      keyInterventions: [
        {
          title: "Unified AWS Organization & Service Management",
          description: "We consolidated all AWS environments under a single AWS Organization. This important step streamlined the management of shared AWS services (including AWS KMS) and allowed for centralized billing. This provided clearer information on service spend patterns.",
          icon: Briefcase
        },
        {
          title: "Intelligent Service Configuration & Scaling",
          description: "We strategically reconfigured development-related AWS managed services to on-demand models where data persistence was key. For other workloads, we implemented cost-effective EC2 instances, ensuring they were correctly sized to match demand and avoid unnecessary costs.",
          icon: Settings
        },
        {
          title: "Proactive Financial Management & AWS Savings Plans",
          description: "A full review of existing AWS Savings Plans was performed. We adjusted these to accurately reflect current and projected infrastructure usage – an area not previously monitored. This change provided immediate cost benefits.",
          icon: DollarSign
        }
      ]
    },
    resultsSection: {
      title: "Project Outcomes: Significant Cost Reduction & Control",
      summary: "The implementation of this solution yielded measurable and sustained results, transforming the client's AWS cost structure and supporting their business growth:",
      highlights: [
        {
          metric: "60%",
          label: "Reduction in Monthly AWS Service Costs",
          icon: CheckCircle
        },
        {
          metric: "Improved",
          label: "Business Profitability from Application",
          icon: CheckCircle
        },
        {
          metric: "Effective",
          label: "Cost Management with Continuous Monitoring System",
          icon: CheckCircle
        }
      ]
    }
  },
  {
    slug: "enterprise-reliability",
    metaTitle: "Case Study: Enterprise System Reliability (99.999% Uptime) - Kult.io",
    metaDescription: "Review how Kult.io resolved system downtime for a major enterprise, achieving 99.999% availability through deployment process optimization.",
    heroImageUrl: "https://picsum.photos/seed/enterprise-uptime-kultio/1200/400",
    pageTitle: "System Reliability Project: Achieving 99.999% Uptime",
    executiveSummary: "A major enterprise, with significant annual revenue, had issues with persistent downtime on their key public-facing system. Kult.io developed and implemented a robust deployment process solution. This project resulted in 99.999% availability, securing company reputation and business operations.",
    clientInfo: {
      name: "Large Enterprise Company",
      industry: "Diversified Public Services",
      challengeSummary: "Daily system downtime on critical landing page, affecting sales and client information access."
    },
    challengeSection: {
      title: "The Issue: System Downtime and Business Impact",
      content: "The client's primary system, the access point for all public sites and key product information, had frequent outages. These incidents, occurring nearly every day for short periods, created problems: negative public perception, lost sales, and client frustration from inability to access important application information. The existing auto-scaled AWS environment was not performing under this pressure, requiring an immediate, expert solution."
    },
    solutionSection: {
      title: "Our Solution: Modernized Deployment Process for System Resilience",
      intro: "Our analysis identified key inefficiencies in the instance startup and deployment process within the auto-scaled environment. The main issue was a full application installation on every new instance, without a correctly configured auto-scaling grace period. Our solution involved several technical improvements:",
      keyInterventions: [
        {
          title: "Optimized Amazon Machine Images (AMIs)",
          description: "We implemented the use of AMIs with all application dependencies pre-installed. This greatly reduced instance launch times, providing a foundation for a rapid-response infrastructure.",
          icon: ShieldCheck
        },
        {
          title: "Automated Deployments with AWS CodeDeploy",
          description: "The application deployment process was re-engineered using AWS CodeDeploy. This ensured consistent, automated updates, reducing manual errors and improving overall deployment time and system reliability.",
          icon: Zap
        },
        {
          title: "Intelligent Auto-Scaling Group Configuration",
          description: "We recalibrated the auto-scaling group's health check grace period and scaling policies. This ensured new instances were fully operational before receiving traffic, preventing issues with unready instances.",
          icon: TrendingUp
        }
      ]
    },
    resultsSection: {
      title: "Project Outcomes: System Stability and Performance",
      summary: "This technical solution delivered significant improvements in system stability and operational performance for the company:",
      highlights: [
        {
          metric: "99.999%",
          label: "Sustained System Availability",
          icon: CheckCircle
        },
        {
          metric: "Zero",
          label: "Unplanned System Downtime Post-Implementation",
          icon: AlertTriangle
        },
        {
          metric: "Improved",
          label: "Client Access & Company Reputation",
          icon: CheckCircle
        }
      ]
    }
  }
];

const SuccessStoryPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const story = stories.find(s => s.slug === slug);

  if (!story) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center text-gray-400">
        <p>Success story not found.</p>
        <RouterLink to="/#projects" className="mt-4 inline-flex items-center text-blue-400 hover:text-blue-300">
          <ArrowLeft className="mr-2 h-5 w-5" /> Back to Success Stories
        </RouterLink>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 text-white">
      <Helmet>
        <title>{story.metaTitle}</title>
        <meta name="description" content={story.metaDescription} />
      </Helmet>

      {story.heroImageUrl && (
        <div className="w-full h-72 md:h-96 lg:h-[500px] overflow-hidden relative">
          <img 
            src={story.heroImageUrl} 
            alt={`${story.pageTitle} hero`} 
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/70 to-transparent"></div>
          <div className="absolute inset-0 flex flex-col justify-end items-center text-center p-8 md:p-12">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 shadow-lg">
              {story.pageTitle}
            </h1>
            <p className="text-lg md:text-xl text-gray-200 max-w-3xl shadow-md">
              {story.executiveSummary}
            </p>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="mb-10 text-center">
            <RouterLink to="/#projects" className="inline-flex items-center text-blue-400 hover:text-blue-300 text-lg">
              <ArrowLeft className="mr-2 h-5 w-5" />
              Back to All Success Stories
            </RouterLink>
        </div>

        {story.clientInfo && (
          <section className="mb-12 md:mb-16 p-6 bg-gray-800 rounded-xl shadow-lg">
            <h2 className="text-2xl md:text-3xl font-bold text-blue-400 mb-4 text-center">Client Information</h2>
            <div className="grid md:grid-cols-3 gap-6 text-center md:text-left">
              <div>
                <h3 className="text-sm uppercase text-gray-400 mb-1">Client</h3>
                <p className="text-lg font-semibold text-white">{story.clientInfo.name}</p>
              </div>
              <div>
                <h3 className="text-sm uppercase text-gray-400 mb-1">Industry</h3>
                <p className="text-lg font-semibold text-white">{story.clientInfo.industry}</p>
              </div>
              <div>
                <h3 className="text-sm uppercase text-gray-400 mb-1">Core Challenge</h3>
                <p className="text-lg font-semibold text-white">{story.clientInfo.challengeSummary}</p>
              </div>
            </div>
          </section>
        )}

        <article className="space-y-12 md:space-y-16">
          <section>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 border-l-4 border-blue-500 pl-4">
              {story.challengeSection.title}
            </h2>
            <div className="prose prose-invert prose-lg max-w-none text-gray-300 prose-p:mb-4 prose-strong:text-white prose-headings:text-white">
              <ReactMarkdown>{story.challengeSection.content}</ReactMarkdown>
            </div>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 border-l-4 border-blue-500 pl-4">
              {story.solutionSection.title}
            </h2>
            <div className="prose prose-invert prose-lg max-w-none text-gray-300 mb-8 prose-p:mb-4 prose-strong:text-white prose-headings:text-white">
              <ReactMarkdown>{story.solutionSection.intro}</ReactMarkdown>
            </div>
            <div className="grid md:grid-cols-1 gap-6 lg:gap-8">
              {story.solutionSection.keyInterventions.map((item, index) => {
                const IconComponent = item.icon || CheckCircle;
                return (
                  <div key={index} className="bg-gray-800 p-6 rounded-lg shadow-lg flex items-start space-x-4 border border-gray-700 hover:border-blue-500 transition-colors duration-300">
                    <div className="flex-shrink-0">
                      <IconComponent className="h-8 w-8 text-blue-400 mt-1" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                      <p className="text-gray-300 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center">
              {story.resultsSection.title}
            </h2>
            <div className="prose prose-invert prose-lg max-w-none text-gray-300 mb-10 text-center prose-p:mb-4 prose-strong:text-white prose-headings:text-white">
              <ReactMarkdown>{story.resultsSection.summary}</ReactMarkdown>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {story.resultsSection.highlights.map((item, index) => {
                const IconComponent = item.icon || CheckCircle;
                return (
                  <div key={index} className="bg-blue-600/30 backdrop-blur-sm p-6 rounded-xl shadow-xl text-center border border-blue-500 transform hover:scale-105 transition-transform duration-300">
                    <IconComponent className="h-12 w-12 text-blue-300 mx-auto mb-4" />
                    <p className="text-3xl md:text-4xl font-bold text-white mb-2">{item.metric}</p>
                    <p className="text-base text-blue-200">{item.label}</p>
                  </div>
                );
              })}
            </div>
          </section>

        </article>
      </div>
    </div>
  );
};

export default SuccessStoryPage; 